import axios, { AxiosError, AxiosRequestConfig, AxiosResponse, Method } from "axios";
import { DirectoryUser, Entity, ImpactArea, RatingDescription, Risk, RiskAssessment, RiskChange, RiskImpactAreaRating, RiskIndicator, ServerAPIResponse, UserLoginResponse, UserProfile } from "./InterfacesServer";
import { AssessmentTypes, RoutePathNames, ReportPageIds, RiskActionStatus, RiskAssessmentStatus, RiskPrioritisationZones, RiskStatus, RiskSortType, RiskGroupFilters, RiskMonitoringLevel } from "./Constants";
import { ReactElement } from "react";
import withReactContent, { ReactSweetAlertOptions } from "sweetalert2-react-content";
import Swal, { SweetAlertResult, SweetAlertInput } from "sweetalert2";
import { store } from "../store/store";
import { setLoggedOnUserProfile } from "../store/loggedOnUserProfile";
import { setRiskRelatedTables } from "../store/riskRelatedTables";
import { infoColor, secondaryColor } from "../layouts/theme";
import { setAppConfig } from "../store/appConfig";
import { setServerAppConfig } from "../store/ServerAppConfig";
import { AppConfig, MainMenuItem, ReportPage } from "./InterfacesClient";


export async function loadAppConfigFromSettingsFile() {
    const dispatch = store.dispatch;
    await fetch("appsettings.json", { cache: 'reload' }).then<AppConfig>(r => r.json())
        .then((data) => {
            const appConfig: AppConfig = {
                SERVER_URL: data.SERVER_URL ?? process.env.REACT_APP_SERVER_URL ?? "",
                ENVIRONMENT: data.ENVIRONMENT ?? process.env.REACT_APP_ENVIRONMENT ?? "",
                APP_VERSION: data.APP_VERSION ?? process.env.REACT_APP_VERSION ?? "",
                APP_LAST_UPDATED: Number(data.APP_LAST_UPDATED ?? process.env.REACT_APP_LAST_UPDATED ?? 0) * 1000,
                APP_PLATFORM: data.APP_PLATFORM ?? process.env.REACT_APP_PLATFORM ?? "",
                REPORTS_BASE_URL: data.REPORTS_BASE_URL ?? process.env.REACT_APP_REPORTS_BASE_URL ?? "",
                SUPPORT_CONTACTS: data.SUPPORT_CONTACTS ?? process.env.REACT_APP_SUPPORT_CONTACTS ?? "",
                USERGUIDE_RISKIDENTIFICATION: data.USERGUIDE_RISKIDENTIFICATION ?? process.env.REACT_APP_USERGUIDE_RISKIDENTIFICATION ?? "",
                USERGUIDE_RISKIDENTIFICATIONASSESSMENT: data.USERGUIDE_RISKIDENTIFICATIONASSESSMENT ?? process.env.REACT_APP_USERGUIDE_RISKIDENTIFICATIONASSESSMENT ?? "",
                USERGUIDE_ENTITYRISKIDENTIFICATION: data.USERGUIDE_ENTITYRISKIDENTIFICATION ?? process.env.REACT_APP_USERGUIDE_ENTITYRISKIDENTIFICATION ?? "",
            }
            dispatch(setAppConfig(appConfig));
        })
        .catch(function (error: AxiosError<ServerAPIResponse<any>>) {
            console.log(error);
        });
}


export async function callServerAPI<Type>(method: Method, url: string, data: any = null, doNotThrowErrorOnFailure: boolean, responseType: import("axios").ResponseType = 'json'): Promise<AxiosResponse<Type>> {
    return await new Promise(async (resolve, reject) => {
        const appConfig = store.getState().appConfig.value;
        let config: AxiosRequestConfig = {}

        config.method = method; config.url = appConfig.SERVER_URL + url; config.withCredentials = true; config.data = data
        if (responseType) config.responseType = responseType
        await axios.request(config)
            .then(function (response: AxiosResponse<Type>) {
                resolve(response)
            })
            .catch(function (error: AxiosError<ServerAPIResponse<any>>) {
                console.log(error);
                if (error?.response?.status === 401) {
                    localStorage.setItem("loginRedirectedFromUrl", window.location.href);
                    let url = new URL(window.location.origin + '/' + RoutePathNames.LOGIN)
                    window.location.replace(url.href);
                }
                else {
                    fireAxiosErrorMySwal(error)
                    if (!doNotThrowErrorOnFailure) reject(error)
                }
            });
    });
}

export async function callServerGetLoggedonUser(): Promise<AxiosResponse<ServerAPIResponse<UserLoginResponse>>> {
    const dispatch = store.dispatch;
    const appConfig = store.getState().appConfig.value;
    return await new Promise(async (resolve, reject) => {
        var config: AxiosRequestConfig = {}
        config.method = 'GET'; config.data = null; config.url = appConfig.SERVER_URL + '/api/UserProfile/GetLoggedonUser'; config.withCredentials = true
        await axios.request(config)
            .then(async function (response: AxiosResponse<ServerAPIResponse<UserLoginResponse>>) {
                dispatch(setRiskRelatedTables(response.data.data.riskRelatedTables));
                dispatch(setLoggedOnUserProfile(response.data.data.userProfile));
                dispatch(setServerAppConfig(response.data.data.serverAppConfig));
                resolve(response)
            })
            .catch(function (error: AxiosError<ServerAPIResponse<any>>) {
                console.log(error);
                reject(error)
            });
    });
}

export function callServerTepngUserLogout() {
    const dispatch = store.dispatch;
    const appConfig = store.getState().appConfig.value;
    dispatch(setLoggedOnUserProfile(null));
    let url = new URL(window.location.origin)
    // needed to logout of microsoft - the frond end url needs to be added to Azure App registration Redirect URIs to allow for logout redirect
    window.location.replace(appConfig.SERVER_URL + '/.auth/logout?post_logout_redirect_uri=' + window.encodeURIComponent(url.href));
}


export async function fireAxiosErrorMySwal(error: AxiosError<ServerAPIResponse<any>>) {
    if (error?.response?.data instanceof Blob && error?.response?.data?.type && error?.response?.data?.type.toLowerCase().indexOf('json') !== -1) {
        var errorString = JSON.parse(await error.response.data.text());
        showAlertError(errorString.message)
    }
    else {
        if (error?.response == null)
            showAlertError(error.message)
        else if (error?.response?.data?.message == null)
            showAlertError(error.response.status + ' ' + error.response.statusText)
        else
            showAlertError(error?.response?.data?.message)
    }
}

export function showAlertError(message: string) {
    const MySwal = withReactContent(Swal);
    MySwal.fire(alertError(message))
}

function alertError(message: string): ReactSweetAlertOptions {
    return {
        icon: 'warning',
        title: '',
        html: `<small>${message}</small>`,
        showDenyButton: true,
        denyButtonText: `<small>OK</small>`,
        showConfirmButton: false,
    }
}

export function showAlertInfo(message: string, title: string = ''): Promise<SweetAlertResult<any>> {
    const MySwal = withReactContent(Swal);
    return MySwal.fire({
        title: `<span style="color:${infoColor}; font-size:16px">${title}</span>`,
        html: `<small> ${message}</small>`,
        icon: 'info',
        iconColor: '#999999',
        showDenyButton: true,
        denyButtonText: `<small>OK</small>`,
        denyButtonColor: infoColor,
        showConfirmButton: false,
    })
}

export function showConfirmBox(message: string, title: string = 'Confirm', yesButtonText: string = 'Yes', noButtonText: string = 'No'): Promise<SweetAlertResult<any>> {
    const MySwal = withReactContent(Swal);
    return MySwal.fire({
        title: `<span style="color:${infoColor}; font-size:16px">${title}</span>`,
        html: `<small> ${message}</small>`,
        icon: 'question',
        iconColor: '#999999',
        cancelButtonColor: infoColor,
        cancelButtonText: noButtonText,
        showCancelButton: true,
        confirmButtonColor: secondaryColor,
        confirmButtonText: yesButtonText

    })
}

export function showInputBox(message: string, title: string = 'Confirm', inputPlaceholder: string = '', okButtonText: string = 'OK', cancelButtonText: string = 'Cancel', inputType: SweetAlertInput = 'textarea'): Promise<SweetAlertResult<any>> {
    const MySwal = withReactContent(Swal);
    return MySwal.fire({
        title: `<span style="font-size:16px">${title}</span>`,
        html: `<small style="color:#cc0000"> ${message}</small>`,
        confirmButtonText: okButtonText,
        confirmButtonColor: secondaryColor,
        cancelButtonColor: infoColor,
        cancelButtonText: cancelButtonText,
        showCancelButton: true,
        input: inputType,
        inputPlaceholder: inputPlaceholder,
        allowEnterKey: true,
        allowEscapeKey: true,
        inputAutoTrim: true,
    })
}


export function showBackDrop(message: string) {
    return {
        title: message,
        html: '<div style="height:150px;width:150px;margin: 0 auto;"><img style="width: 100px;" src="../assets/images/loading-icon-red.gif" /><br> <strong> Please Wait... </strong> </div>', showConfirmButton: false, showCancelButton: false, allowOutsideClick: false,
    }
}

export function getAssessmentTypeString(assessmentType: string) {
    switch (assessmentType) {
        case AssessmentTypes.AFFILIATE: return 'Affiliate';
        case AssessmentTypes.DRILL: return 'Drill';
        default: return null;
    }
}

export function getRiskStatusString(status: string) {
    switch (status) {
        case RiskStatus.DRAFT: return 'Draft';
        case RiskStatus.AWAITING_ENTITY_HEAD_REVIEW: return 'Awaiting Entity Head Review';
        case RiskStatus.SUBMITTED: return 'Submitted';
        case RiskStatus.APPROVED: return 'Approved';
        case RiskStatus.CHAMPION_NOMINATED: return 'Champion Nominated';
        case RiskStatus.CHAMPION_NOMINATION_REJECTED: return 'Champion Nomination Rejected';
        case RiskStatus.ASSIGNED_TO_CHAMPION: return 'Assigned to Champion';
        case RiskStatus.ACTION_PLAN_SUBMITTED: return 'Action Plan Submitted';
        case RiskStatus.ACTION_PLAN_REJECTED: return 'Action Plan Rejected';
        case RiskStatus.ACTION_PLAN_APPROVED: return 'Action Plan Approved';
        case RiskStatus.ACTION_PLAN_IMPLEMENTATION_SUBMITTED: return 'Action Plan Implementation Submitted';
        case RiskStatus.ACTION_PLAN_IMPLEMENTATION_REJECTED_BY_SPONSOR: return 'Action Plan Implementation Rejected';
        case RiskStatus.UNEXPOSED: return 'Unexposed'
        default: return null;
    }
}

export function getRiskAssessmentStatusString(status: string) {
    switch (status) {
        case RiskAssessmentStatus.NEW: return 'New';
        case RiskAssessmentStatus.IDENTIFICATION: return 'Identification';
        case RiskAssessmentStatus.AGGREGATION: return 'Aggregation';
        case RiskAssessmentStatus.VOTING: return 'Voting';
        case RiskAssessmentStatus.APPROVAL: return 'Approval';
        case RiskAssessmentStatus.CLOSED: return 'Closed';
        default: return null;
    }
}

export function getRiskActionStatusString(status: string, risk: Risk) {
    switch (status) {
        case RiskActionStatus.DRAFT: return 'Draft';
        case RiskActionStatus.SUBMITTED: return 'Submitted';
        case RiskActionStatus.REJECTED: return 'Rejected';
        case RiskActionStatus.APPROVED: return 'Approved';
        case RiskActionStatus.IMPLEMENTATION_APPROVED: return 'Implementation Approved';
        default: return null;
    }
}

export function getRiskPrioritisationZoneString(prioritisation: string) {
    switch (prioritisation) {
        case RiskPrioritisationZones.DELEGATION_ZONE: return 'Delegation Zone';
        case RiskPrioritisationZones.VIGILANCE_ZONE: return 'Vigilance Zone';
        case RiskPrioritisationZones.VERIFICATION_ZONE: return 'Verification Zone';
        case RiskPrioritisationZones.PRIORITY_REMEDIATION: return 'Priority Remediation Zone';
        default: return null;
    }
}

export function getRiskPrioritisationZoneNumber(prioritisation: string) {
    switch (prioritisation) {
        case RiskPrioritisationZones.DELEGATION_ZONE: return 1;
        case RiskPrioritisationZones.VIGILANCE_ZONE: return 2;
        case RiskPrioritisationZones.VERIFICATION_ZONE: return 3;
        case RiskPrioritisationZones.PRIORITY_REMEDIATION: return 4;
        default: return 0;
    }
}

export function getUserDefaultEntity(entities: Entity[], directoryUser: DirectoryUser | null | undefined) {
    const userRadicalShort = directoryUser?.department
    let userEntities: Entity[] = []
    if (userRadicalShort != null && userRadicalShort.trim() !== '') {
        userEntities = entities.filter(e => !e.disabled && userRadicalShort.toUpperCase().startsWith(e.baseRadicalShort.toUpperCase()));
    }
    if (userEntities.length === 0) {
        userEntities = entities.filter(e => !e.disabled);
    }
    return userEntities[0];
}



/**
 * Returns the description of the rating of an impact area of a Risk.
 */
export function getImpactAreaRatingDescription(riskImpactArea: RiskImpactAreaRating | null | undefined, impactArea: ImpactArea): string {
    switch (riskImpactArea?.rating) {
        case 6:
            return riskImpactArea.rating + " - " + impactArea.rating6Description;
        case 5:
            return riskImpactArea.rating + " - " + impactArea.rating5Description;
        case 4:
            return riskImpactArea.rating + " - " + impactArea.rating4Description;
        case 3:
            return riskImpactArea.rating + " - " + impactArea.rating3Description;
        case 2:
            return riskImpactArea.rating + " - " + impactArea.rating2Description;
        case 1:
            return riskImpactArea.rating + " - " + impactArea.rating1Description;
        default:
            return 'N/A';
    }
}

/**
 * Returns the description for the Impact Likelihood or Mitigation overall rating for a Risk.
 */
export function getRatingDescription(rating: number, ratingDescriptions: RatingDescription[], ratingType: string): string {
    var ratingDescription = ratingDescriptions.find(r => r.ratingType === ratingType)
    if (ratingDescription == null) return ''
    switch (rating) {
        case 6:
            return ratingDescription.rating6Description;
        case 5:
            return ratingDescription.rating5Description;
        case 4:
            return ratingDescription.rating4Description;
        case 3:
            return ratingDescription.rating3Description;
        case 2:
            return ratingDescription.rating2Description;
        case 1:
            return ratingDescription.rating1Description;
        default:
            return 'N/A';
    }
}

/**
 * Returns the description for the Impact Likelihood or Mitigation overall rating for a Risk.
 */
export function getRatingDescriptionHtml(rating: number, ratingDescriptions: RatingDescription[], ratingType: string): JSX.Element {
    var ratingDescription = ratingDescriptions.find(r => r.ratingType === ratingType)
    if (ratingDescription == null) return <></>
    var description = ''
    switch (rating) {
        case 6:
            description = ratingDescription.rating6Description
            break;
        case 5:
            description = ratingDescription.rating5Description;
            break;
        case 4:
            description = ratingDescription.rating4Description;
            break;
        case 3:
            description = ratingDescription.rating3Description;
            break;
        case 2:
            description = ratingDescription.rating2Description;
            break;
        case 1:
            description = ratingDescription.rating1Description;
            break;
        default:
            description = 'N/A';
    }

    return (
        <ul>
            {description.trim().split('\n').map(e => e.trim() === '' ? '' : <li style={{ marginBottom: 10 }} > {e} </li>)}
        </ul>
    )
}


export function checkGlobalMenuEnabled(mainMenuItem: MainMenuItem, userLoggedOn: UserProfile, entities: Entity[]): boolean {
    switch (mainMenuItem.pathName) {
        case RoutePathNames.RISK_IDENTIFICATION:
            return true;
        case RoutePathNames.RISK_ASSESSMENT:
            return userLoggedOn.isERMTeamMember;
        case RoutePathNames.RISK_ASSIGNMENT:
            return userLoggedOn.isERMTeamMember;
        case RoutePathNames.RISK_TREATMENT:
            return true;
        case RoutePathNames.RISK_MONITORING:
            return true;
        case RoutePathNames.REPORTS:
            return userLoggedOn.isERMTeamMember || userLoggedOn.riskMonitoringSheetDisplayAll || checkUserIsEntityHead(entities, userLoggedOn) || checkUserIsEntityCorrespondent(entities, userLoggedOn) || checkUserIsEntityDirector(entities, userLoggedOn);
        case RoutePathNames.EMAIL_TEMPLATES:
            return userLoggedOn.isERMTeamMember;
        case RoutePathNames.CATEGORIES:
            return userLoggedOn.isERMTeamMember;
        case RoutePathNames.BUSINESS_OBJECTIVES:
            return userLoggedOn.isERMTeamMember;
        case RoutePathNames.ENTITIES:
            return userLoggedOn.isERMTeamMember;
        case RoutePathNames.RATINGS:
            return userLoggedOn.isERMTeamMember;
        case RoutePathNames.IMPACTAREAS:
            return userLoggedOn.isERMTeamMember;
        case RoutePathNames.USERPROFILES:
            return userLoggedOn.isAdmin;
        default:
            return true
    }
}


export function checkReportPageEnabled(reportPage: ReportPage, entities: Entity[], userLoggedOn: UserProfile): boolean {
    switch (reportPage.pageId) {
        case ReportPageIds.RISKS_UNIVERSE:
            return checkUserIsEntityDirector(entities, userLoggedOn) || userLoggedOn.isERMTeamMember || userLoggedOn.riskMonitoringSheetDisplayAll;
        case ReportPageIds.FREQUENCY_OF_RISK_MENTIONS:
            return checkUserIsEntityDirector(entities, userLoggedOn) || userLoggedOn.isERMTeamMember || userLoggedOn.riskMonitoringSheetDisplayAll;
        case ReportPageIds.RISK_ANALYSIS:
            return checkUserIsEntityDirector(entities, userLoggedOn) || userLoggedOn.isERMTeamMember || userLoggedOn.riskMonitoringSheetDisplayAll;
        case ReportPageIds.RISK_CONSENSUS_OUTPUT:
            return checkUserIsEntityDirector(entities, userLoggedOn) || userLoggedOn.isERMTeamMember || userLoggedOn.riskMonitoringSheetDisplayAll;
        case ReportPageIds.RISK_PRIORITISATION_DETAILS:
            return checkUserIsEntityDirector(entities, userLoggedOn) || userLoggedOn.isERMTeamMember || userLoggedOn.riskMonitoringSheetDisplayAll;
        case ReportPageIds.RISK_VOTING_DISPERSION:
            return checkUserIsEntityDirector(entities, userLoggedOn) || userLoggedOn.isERMTeamMember || userLoggedOn.riskMonitoringSheetDisplayAll;
        case ReportPageIds.RISK_VOTING_BREAKDOWN:
            return userLoggedOn.isERMTeamMember;
        case ReportPageIds.RISK_VOTING_CONSENSUS:
            return checkUserIsEntityDirector(entities, userLoggedOn) || userLoggedOn.isERMTeamMember || userLoggedOn.riskMonitoringSheetDisplayAll;
        case ReportPageIds.RISK_REGISTER:
            return checkUserIsEntityCorrespondent(entities, userLoggedOn) || checkUserIsEntityHead(entities, userLoggedOn) || checkUserIsEntityDirector(entities, userLoggedOn) || userLoggedOn.isERMTeamMember || userLoggedOn.riskMonitoringSheetDisplayAll;
        case ReportPageIds.GLOBAL_EXPOSURE_DASHBOARD:
            return checkUserIsEntityDirector(entities, userLoggedOn) || userLoggedOn.isERMTeamMember || userLoggedOn.riskMonitoringSheetDisplayAll;
        case ReportPageIds.RISK_TREND:
            return checkUserIsEntityCorrespondent(entities, userLoggedOn) || checkUserIsEntityHead(entities, userLoggedOn) || checkUserIsEntityDirector(entities, userLoggedOn) || userLoggedOn.isERMTeamMember || userLoggedOn.riskMonitoringSheetDisplayAll;
        default:
            return false;
    }
}

export function canSubmitRisk(risk: Risk, user: UserProfile | null | undefined): boolean {
    if (user == null) return false
    var allowSubmit = false;
    if (risk.status === RiskStatus.DRAFT && risk.sourceAssessment != null && (risk.createdBy == null || risk.createdBy === user.id)) allowSubmit = true;
    // if (val) checks that val has a truthy value. falsy values are: null, undefined, NaN, "", 0, false
    return Boolean(allowSubmit && risk.name && risk.description && risk.businessObjectiveImpacted != null && risk.category != null && risk.riskIndicators.filter(f => f.indicatorText != null && f.indicatorText.trim() !== "").length !== 0 && risk.originatingEntity != null && risk.impact && risk.likelihood && risk.mitigation && (risk.mitigation === 6 || risk.existingMitigations))
}

export function canSendRiskForEntityHeadReview(risk: Risk, user: UserProfile | null | undefined): boolean {
    if (user == null) return false
    var allowSendForReview = false;
    if (risk.status === RiskStatus.DRAFT && risk.sourceAssessment == null && checkUserIsEntityCorrespondent([risk.originatingEntity], user)) allowSendForReview = true;
    // if (val) checks that val has a truthy value. falsy values are: null, undefined, NaN, "", 0, false
    return Boolean(allowSendForReview && risk.name && risk.description && risk.businessObjectiveImpacted != null && risk.category != null && risk.riskIndicators.filter(f => f.indicatorText != null && f.indicatorText.trim() !== "").length !== 0 && risk.originatingEntity != null && risk.impact && risk.likelihood && risk.mitigation && (risk.mitigation === 6 || risk.existingMitigations))
}

export function canReviewRisk(risk: Risk, user: UserProfile | null | undefined): boolean {
    if (user == null) return false
    var allowReview = false;
    if (risk.status === RiskStatus.AWAITING_ENTITY_HEAD_REVIEW && checkUserIsEntityHead([risk.originatingEntity], user)) allowReview = true;
    // if (val) checks that val has a truthy value. falsy values are: null, undefined, NaN, "", 0, false
    return Boolean(allowReview && risk.name && risk.description && risk.businessObjectiveImpacted != null && risk.category != null && risk.riskIndicators.filter(f => f.indicatorText != null && f.indicatorText.trim() !== "").length !== 0 && risk.originatingEntity != null && risk.impact && risk.likelihood && risk.mitigation && (risk.mitigation === 6 || risk.existingMitigations))
}



export function checkRiskCreateable(riskAssessmentForRiskIdentification: RiskAssessment | null, userLoggedOn: UserProfile): boolean {
    // anyone can add a risk in risk drill mode
    if (riskAssessmentForRiskIdentification == null) return true
    // allow to add in risk assessment if riskassessment is in identification phase and user is an identfication responder/ERM Team member
    if (riskAssessmentForRiskIdentification.status === RiskAssessmentStatus.IDENTIFICATION && riskAssessmentForRiskIdentification.identificationEndDate != null && new Date(riskAssessmentForRiskIdentification.identificationEndDate) >= new Date() && (riskAssessmentForRiskIdentification.identificationParticipants.find(r => r.userId === userLoggedOn.id) != null || userLoggedOn.isERMTeamMember)) return true;
    return false;
}

export function checkRiskEditable(risk: Risk, riskAssessmentForRiskIdentification: RiskAssessment | null, userLoggedOn: UserProfile): boolean {
    //if risk is in a riskassessment but the page wasnt loaded in a risk assessment identification mode
    if (risk.sourceAssessment != null && riskAssessmentForRiskIdentification == null) return false


    if (risk.sourceAssessment != null) {
        // allow to update risk in risk assessment if 
        // page was loaded in risk assessment identification mode and
        // the risk is for the loaded risk assessment and
        // riskassessment is in identification phase and
        // risk is in draft status and 
        // user is original identifier
        if (riskAssessmentForRiskIdentification !== null && riskAssessmentForRiskIdentification.id === risk.sourceAssessment.id && riskAssessmentForRiskIdentification.status === RiskAssessmentStatus.IDENTIFICATION && riskAssessmentForRiskIdentification.identificationEndDate != null && new Date(riskAssessmentForRiskIdentification.identificationEndDate) >= new Date() && risk.status === RiskStatus.DRAFT && risk.createdBy === userLoggedOn.id) return true;
    }
    else {
        // allow to update risk drill if 
        // risk is in draft status and user is original identifier or 
        // risk is in draft status and user is Correspondent or
        // risk is in awaiting entity head review status and user is EntityHead
        if (risk.status === RiskStatus.DRAFT && risk.createdBy === userLoggedOn.id) return true;
        let isCorrespondentForRisk = checkUserIsEntityCorrespondent([risk.originatingEntity], userLoggedOn);
        let isEntityHeadForRisk = checkUserIsEntityHead([risk.originatingEntity], userLoggedOn);
        if (risk.status === RiskStatus.DRAFT && isCorrespondentForRisk) return true;
        if (risk.status === RiskStatus.AWAITING_ENTITY_HEAD_REVIEW && isEntityHeadForRisk) return true;
    }
    return false;
}

export function checkRiskDeleteable(risk: Risk, riskAssessmentForRiskIdentification: RiskAssessment | null, userLoggedOn: UserProfile): boolean {
    return checkRiskEditable(risk, riskAssessmentForRiskIdentification, userLoggedOn);
}

export function checkSubmitRiskChangeValidation(riskChange: RiskChange): boolean {
    // if (val) checks that val has a truthy value. falsy values are: null, undefined, NaN, "", 0, false
    return Boolean(riskChange.changeJustification && riskChange.name && riskChange.description && riskChange.businessObjectiveImpacted != null && riskChange.category != null && riskChange.riskMonitoringLevel != null && riskChange.originatingEntity != null && riskChange.impact && riskChange.likelihood && riskChange.mitigation && (riskChange.mitigation === 1 || riskChange.existingMitigations))
}

export function checkUserIsEntityCorrespondent(entities: Entity[], user: UserProfile | null | undefined): boolean {
    if (user == null) return false
    return entities?.find(e => e?.riskCorrespondents?.find(c => c.userId === user.id) != null) != null
}

export function getCorrespondentEntitiesForLoggedOnUser(entities: Entity[], user: UserProfile | null | undefined): Entity[] {
    if (user == null) return []
    return entities?.filter(e => e?.riskCorrespondents?.find(c => c.userId === user.id) != null)
}

export function checkUserIsEntityHead(entities: Entity[], user: UserProfile | null | undefined): boolean {
    if (user == null) return false
    return entities?.find(e => e?.heads?.find(c => c.userId === user.id) != null) != null
}

export function getEntityHeadEntitiesForLoggedOnUser(entities: Entity[], user: UserProfile | null | undefined): Entity[] {
    if (user == null) return []
    return entities?.filter(e => e?.heads?.find(c => c.userId === user.id) != null)
}

export function checkUserIsRiskChampion(risk: Risk, user: UserProfile | null | undefined): boolean {
    if (user == null) return false
    return risk.champion === user.id || risk.alternateChampion === user.id
}

export function checkUserIsEntityDirector(entities: Entity[], user: UserProfile | null | undefined): boolean {
    if (user == null) return false
    return entities?.find(e => e?.director === user.id) != null
}

export function getDefaultSponsor(risk: Risk) {
    if (risk.riskMonitoringLevel === RiskMonitoringLevel.Entity && risk.originatingEntity.heads?.length > 0)
        return [risk.originatingEntity.heads[0].userId, risk.originatingEntity.heads[0].userEmail];
    else
        return [risk.originatingEntity.director, risk.originatingEntity.directorEmail];
}

export function getDefaultChampion(risk: Risk) {
    if (risk.riskMonitoringLevel === RiskMonitoringLevel.Entity && risk.originatingEntity.riskCorrespondents?.length > 0)
        return [risk.originatingEntity.riskCorrespondents[0].userId, risk.originatingEntity.riskCorrespondents[0].userEmail];
    else if (risk.originatingEntity.heads?.length > 0)
        return [risk.originatingEntity.heads[0].userId, risk.originatingEntity.heads[0].userEmail];
    else
        return [risk.originatingEntity.director, risk.originatingEntity.directorEmail];
}

/**
 * Returns a descriptive message for a server error
 */
export function getDescriptiveErrorMessage(error: AxiosError<ServerAPIResponse<any>>): ReactElement {
    console.log(error)
    if (error.response === undefined) return <span>{error.toString()}</span>;
    return <span>{error.response.data.message ?? error.response.statusText + ' - ' + error.response.data}</span>
}

/**
 * Returns a plain span tag with text as InnerText
 */
export function generatePlainElementFromText(text: string): ReactElement {
    return <span>{text}</span>
}

export function getEntitiesListAsSpan(entities: Entity[], separator: string, extraSpaces: number): ReactElement[] {
    let html = []
    for (let i = 0; i < entities.length; i++)
        html.push(<span key={entities[i].id} title={entities[i].baseRadicalShort}>{entities[i].name + (i < entities.length - 1 ? separator : '')} {'\xa0'.repeat(extraSpaces)} </span>)
    return html
}

export function getUsersListAsSpan(users: DirectoryUser[], separator: string, extraSpaces: number): ReactElement[] | undefined {
    if (users == null) return
    let html = []
    for (let i = 0; i < users.length; i++)
        html.push(<span key={users[i].email} title={users[i].email}>{users[i].firstName + ' ' + users[i].lastName + (i < users.length - 1 ? separator : '')} {'\xa0'.repeat(extraSpaces)} </span>)
    return html
}

export function convertHtmlToText(inputText: string): string {
    var returnText = "" + inputText;

    //-- remove BR tags and replace them with line break
    returnText = returnText.replace(/<br>/gi, "\n");
    returnText = returnText.replace(/<br\s\/>/gi, "\n");
    returnText = returnText.replace(/<br\/>/gi, "\n");

    //-- remove P and A tags but preserve what's inside of them
    returnText = returnText.replace(/<p.*>/gi, "\n");
    returnText = returnText.replace(/<a.*href="(.*?)".*>(.*?)<\/a>/gi, " $2 ($1)");

    //-- remove all inside SCRIPT and STYLE tags
    returnText = returnText.replace(/<script.*>[\w\W]{1,}(.*?)[\w\W]{1,}<\/script>/gi, "");
    returnText = returnText.replace(/<style.*>[\w\W]{1,}(.*?)[\w\W]{1,}<\/style>/gi, "");
    //-- remove all else
    returnText = returnText.replace(/<(?:.|\s)*?>/g, "");

    //-- get rid of more than 2 multiple line breaks:
    returnText = returnText.replace(/(?:(?:\r\n|\r|\n)\s*){2,}/gim, "\n\n");

    //-- get rid of more than 2 spaces:
    returnText = returnText.replace(/ +(?= )/g, '');

    //-- get rid of html-encoded characters:
    returnText = returnText.replace(/&nbsp;/gi, " ");
    returnText = returnText.replace(/&amp;/gi, "&");
    returnText = returnText.replace(/&quot;/gi, '"');
    returnText = returnText.replace(/&lt;/gi, '<');
    returnText = returnText.replace(/&gt;/gi, '>');

    return returnText;
}


export function truncateTextToWhiteSpace(text: string, numChars: number): string {
    if (text.length <= numChars)
        return text
    var truncatedText = text.substring(0, numChars)
    return truncatedText.substring(0, truncatedText.lastIndexOf(' ')) + " ..."
}

export function formatNumberWithCommas(x: number | string, numDecimalPlaces: number): string {
    if (typeof x === 'string') x = parseFloat(x);
    if (isNaN(x)) return '';
    const options = {
        minimumFractionDigits: 0,
        maximumFractionDigits: numDecimalPlaces, // Ensures no more than two decimal places
    };
    return x.toLocaleString('en-US', options);
}

export function formatLongDateString(dtStr: Date, includeTime = false): string {
    var dateOptions: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'long', day: '2-digit' };
    var timeOptions: Intl.DateTimeFormatOptions = { hour: '2-digit', minute: '2-digit', hour12: true };
    var dt = new Date(dtStr)
    return dt.toLocaleDateString('en-GB', dateOptions) + (includeTime ? " " + dt.toLocaleTimeString('en-GB', timeOptions) : '')
}

export function formatShortDateString(dtStr: Date, includeTime = false): string {
    var dateOptions: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'short', day: '2-digit' };
    var timeOptions: Intl.DateTimeFormatOptions = { hour: '2-digit', minute: '2-digit', hour12: true };
    var dt = new Date(dtStr)
    return dt.toLocaleDateString('en-GB', dateOptions) + (includeTime ? " " + dt.toLocaleTimeString('en-GB', timeOptions) : '')
}

export function formatTimeString(dtStr: Date): string {
    var timeOptions: Intl.DateTimeFormatOptions = { hour: '2-digit', minute: '2-digit', hour12: true };
    var dt = new Date(dtStr)
    return dt.toLocaleTimeString('en-GB', timeOptions)
}

export function getCurrentTimeString() {
    const date = new Date()
    return ('0' + (date.getUTCHours())).slice(-2) + ':' + ('0' + (date.getUTCMinutes())).slice(-2) + ':' + ('0' + (date.getUTCSeconds())).slice(-2) + ':' + date.getUTCMilliseconds()
}

export function formatDateyyyymmdd(date: Date | null | undefined) {
    if (date == null) return ""
    date = new Date(date);
    const day = `${date.getDate() < 10 ? '0' : ''}${date.getDate()}`;
    const month = `${date.getMonth() + 1 < 10 ? '0' : ''}${date.getMonth() + 1}`;
    const year = (date.getFullYear() + '').padStart(4, '0')
    const hour = (date.getHours() + '').padStart(2, '0')
    const min = (date.getMinutes() + '').padStart(2, '0')
    return `${year}-${month}-${day}T${hour}:${min}`;
}

export function getFilteredAndSortedRisks(loggedOnUserProfile: UserProfile | null, risks: Risk[], entityFilter: string, categoryFilter: string, statusFilter: string, riskAssessmentFilter: string, prioritisationFilter: string, riskGroupFilter: RiskGroupFilters, riskSortType: RiskSortType) {
    let _filteredRisks = risks.filter(risk =>
        (entityFilter === 'All' || risk.originatingEntity.id === Number(entityFilter)) &&
        (categoryFilter === 'All' || risk.category?.id === Number(categoryFilter)) &&
        (statusFilter === 'All' || risk.status === statusFilter) &&
        (prioritisationFilter === 'All' || risk.prioritisation === prioritisationFilter) &&
        (riskAssessmentFilter === 'All' || (risk.sourceAssessment?.id ?? 0) === Number(riskAssessmentFilter)) &&
        (
            riskGroupFilter === RiskGroupFilters.GLOBAL_RISKS ||
            (riskGroupFilter === RiskGroupFilters.MY_RISKS && risk.createdBy === loggedOnUserProfile?.id) ||
            (riskGroupFilter === RiskGroupFilters.ENTITY_RISKS && (checkUserIsEntityHead([risk.originatingEntity], loggedOnUserProfile) || checkUserIsEntityCorrespondent([risk.originatingEntity], loggedOnUserProfile))) ||
            (riskGroupFilter === RiskGroupFilters.RISKS_AWAITING_ENTITY_HEAD_REVIEW && (risk.status === RiskStatus.AWAITING_ENTITY_HEAD_REVIEW) && (checkUserIsEntityHead([risk.originatingEntity], loggedOnUserProfile) || checkUserIsEntityCorrespondent([risk.originatingEntity], loggedOnUserProfile)))
        )
    )
    return sortRisks(_filteredRisks, riskSortType)
}

export function sortRisks(risks: Risk[], riskSortType: RiskSortType) {
    switch (riskSortType) {
        case RiskSortType.SortDateCreatedDesc:
            return risks.sort((a, b) => (a.submittedDate == null && b.submittedDate == null) ? (a.createdDate > b.createdDate ? -1 : 1) : ((b.submittedDate == null || a.submittedDate! > b.submittedDate) ? -1 : 1));
        case RiskSortType.SortDateCreatedAsc:
            return risks.sort((a, b) => (a.submittedDate == null && b.submittedDate == null) ? (a.createdDate > b.createdDate ? 1 : -1) : ((b.submittedDate == null || a.submittedDate! > b.submittedDate) ? 1 : -1));
        case RiskSortType.SortNameDesc:
            return risks.sort((a, b) => a.name.toLowerCase() > b.name.toLowerCase() ? -1 : 1);
        case RiskSortType.SortNameAsc:
            return risks.sort((a, b) => a.name.toLowerCase() < b.name.toLowerCase() ? -1 : 1);
        default:
            return risks;
    }
}

export function isNullOrWhiteSpace(str: string | null | undefined) {
    if (str == null) return true
    return Boolean(str) === false || Boolean(str.trim()) === false
}

export function GetIndicatorColor(indicator: RiskIndicator): string {
    if (indicator.higherIsBetter) {
        if (indicator.currentValue < indicator.lowerThreshold) return "#C00000";
        if (indicator.currentValue >= indicator.upperThreshold) return "#00642d";
        if (indicator.currentValue >= indicator.lowerThreshold && indicator.currentValue < indicator.upperThreshold) return "#FFCC00";
    }
    else {
        if (indicator.currentValue > indicator.upperThreshold) return "#C00000";
        if (indicator.currentValue <= indicator.lowerThreshold) return "#00642d";
        if (indicator.currentValue <= indicator.upperThreshold && indicator.currentValue > indicator.lowerThreshold) return "#FFCC00";
    }
    return "#555555";
}
